/* eslint-disable prefer-destructuring */
import { alpha, createTheme, darken, lighten, responsiveFontSizes, } from '@mui/material';
const settings = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 600,
            md: 900,
            lg: 1200,
            xl: 1536,
            xxl: 2100,
        },
    },
    typography: {
        fontFamily: 'var(--side-font-family)',
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightMedium: 500,
        fontWeightSemiBold: 600,
        fontWeightBold: 700,
        h1: {
            fontSize: '2rem',
            lineHeight: 1.4,
            letterSpacing: '0.25px',
            fontWeight: 600,
        },
        h2: {
            fontSize: '1.438rem',
            lineHeight: 1.4,
            letterSpacing: '0.25px',
            fontWeight: 600,
        },
        h3: {
            fontSize: '1.25rem',
            lineHeight: 1.167,
            fontWeight: 500,
        },
        h4: {
            fontSize: '1rem',
            lineHeight: 1.4,
            fontWeight: 600,
            letterSpacing: '0.25px',
        },
        h5: {
            fontSize: '.875rem',
            lineHeight: 1.4,
            fontWeight: 600,
            letterSpacing: '0.25px',
        },
        h6: {
            fontSize: '.813rem',
            lineHeight: 1.4,
            letterSpacing: '0.25px',
            fontWeight: 600,
        },
        subtitle1: {
            fontSize: '1rem',
            lineHeight: 1.4,
            letterSpacing: '0.15px',
            fontWeight: 500,
        },
        subtitle2: {
            fontSize: '0.875rem',
            lineHeight: 1.4,
            letterSpacing: '0.15px',
            fontWeight: 500,
        },
        body1: {
            fontSize: '1rem',
            lineHeight: 1.4,
            letterSpacing: '0.15px',
            fontWeight: 400,
        },
        body2: {
            fontSize: '0.875rem',
            lineHeight: 1.4,
            letterSpacing: '0.15px',
            fontWeight: 400,
        },
        body3: {
            fontSize: '0.813rem',
            lineHeight: 1.4,
            fontWeight: 400,
            fontFamily: 'var(--side-font-family)',
            letterSpacing: '0.15px',
        },
        caption: {
            fontSize: '0.813rem',
            lineHeight: 1.4,
            letterSpacing: '0.4px',
            fontWeight: 500,
        },
        button: {
            fontSize: '0.938rem',
            lineHeight: '1.75rem',
            textTransform: 'none',
            fontWeight: 600,
        },
        overline: {
            fontSize: '0.813rem',
            lineHeight: 1.4,
            letterSpacing: '1px',
            textTransform: 'uppercase',
            fontWeight: 600,
        },
    },
    palette: {
        primary: {
            main: '#1860CF',
            light: '#EDF2FB',
            dark: '#104390',
            contrastText: '#FFFFFF',
        },
        secondary: {
            main: '#B861D2',
            light: '#FAF4FC',
            dark: '#773A8A',
            contrastText: '#FFFFFF',
        },
        text: {
            primary: '#272B36',
            secondary: '#606881',
            disabled: '#A6AAB5',
        },
        action: {
            active: '#686C75',
            hover: '#F3F3F5',
            selected: '#EDF2FB',
            disabled: '#A6AAB5',
            disabledBackground: '#DBDEE4',
            focus: '#F3F3F5',
        },
        other: {
            outlineBorder: '#CACCD3',
            disabledOutlineBorder: '#E4E5E9',
            spaceCadet: '#202846',
        },
        error: {
            main: '#B1272B',
            dark: '#54191B',
            light: '#F7E9EA',
            contrastText: '#FFFFFF',
        },
        warning: {
            main: '#FCB325',
            dark: '#272B36',
            light: '#FFF1D6',
            contrastText: '#3B3D3D',
        },
        success: {
            main: '#328536',
            dark: '#1D3F1E',
            light: '#EDF5ED',
            contrastText: '#FFFFFF',
        },
        info: {
            main: '#1860CF',
            dark: '#104390',
            light: '#EDF2FB',
            contrastText: '#FFFFFF',
        },
        utility: {
            main: '#FFFFFF',
            dark: '#F3F3F5',
            light: '#FFFFFF',
            contrastText: '#606881',
        },
        background: {
            default: '#FAFAFC',
            paper: '#FFF',
        },
        grey: {
            A100: '#D5D5D5',
            A200: '#AAAAAA',
            A400: '#616161',
            A700: '#303030',
        },
        accent: {
            slate: '#4E556C',
            violet: '#665191',
            plum: '#A05195',
            neonBlue: '#6366F1',
        },
    },
    components: {
        MuiBreadcrumbs: {
            styleOverrides: {
                root: ({ theme: internalTheme }) => ({
                    '& a': {
                        color: 'inherit',
                        textDecoration: 'none',
                    },
                    '& a:hover': {
                        textDecoration: 'underline',
                    },
                    '& li:last-of-type': {
                        color: internalTheme.palette.text.primary,
                    },
                }),
            },
        },
        MuiInputLabel: {
            styleOverrides: {
                root: ({ theme: internalTheme }) => ({
                    fontSize: internalTheme.typography.pxToRem(13),
                    fontWeight: internalTheme.typography.fontWeightMedium,
                    '&.Mui-focused, &.Mui-error': {
                        color: internalTheme.palette.text.primary,
                    },
                    '&.highlight-secondary:not(.Mui-error, .Mui-disabled) .MuiFormLabel-asterisk': {
                        color: internalTheme.palette.secondary.main,
                    },
                }),
            },
        },
        MuiAppBar: {
            styleOverrides: {
                colorPrimary({ theme: internalTheme }) {
                    return {
                        backgroundColor: internalTheme.palette.other.spaceCadet,
                    };
                },
                root: {
                    boxShadow: 'none',
                },
            },
        },
        MuiAvatar: {
            styleOverrides: {
                root: {
                    fontSize: '1.25rem',
                    lineHeight: 1,
                    letterSpacing: '0.14px',
                },
            },
        },
        MuiCard: {
            defaultProps: {
                variant: 'outlined',
            },
            variants: [
                {
                    props: { variant: 'outlined' },
                    style: ({ theme }) => ({
                        borderRadius: 2,
                        borderWidth: 1.5,
                        borderColor: theme.palette.grey[300],
                    }),
                },
            ],
        },
        MuiAutocomplete: {
            defaultProps: {
                slotProps: {
                    chip: { variant: 'outlined', size: 'small' },
                },
            },
            styleOverrides: {
                tag: {
                    maxWidth: '16rem',
                },
                input: {
                    padding: '0 !important',
                    marginRight: '2.5px',
                    minHeight: '22px',
                },
                root: {
                    '&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon': {
                        '& .MuiInputBase-root': {
                            paddingRight: '69px !important',
                        },
                    },
                    '& .MuiAutocomplete-inputRoot': {
                        minHeight: '44px',
                        '& .MuiAutocomplete-input': {
                            minWidth: '200px',
                        },
                    },
                    '& .MuiChip-root': {
                        margin: 0,
                    },
                    '& .MuiInputBase-root': {
                        gap: '8px',
                        paddingLeft: '12px !important',
                        paddingRight: '12px !important',
                        paddingTop: '10px !important',
                        paddingBottom: '10px !important',
                    },
                },
            },
        },
        MuiBadge: {
            styleOverrides: {
                root({ ownerState: { variant }, theme }) {
                    const styles = {};
                    if (variant === 'inline') {
                        styles.display = 'flex';
                        styles.width = 'fit-content';
                        styles.alignItems = 'center';
                        styles.justifyContent = 'center';
                        styles.gap = theme.spacing(0.5);
                    }
                    styles['&.disabled'] = {
                        '& .MuiBadge-badge': {
                            backgroundColor: theme.palette.action.disabledBackground,
                            color: theme.palette.text.disabled,
                        },
                    };
                    return styles;
                },
                badge({ ownerState: { variant, color }, theme }) {
                    const { palette } = theme;
                    // Make badge count semi-bold
                    const styles = {
                        fontWeight: theme.typography.fontWeightSemiBold,
                    };
                    // Color overrides for default
                    if (color === 'default') {
                        styles.backgroundColor = palette.grey[200];
                        styles.color = palette.text.secondary;
                        if (variant === 'dot') {
                            styles.backgroundColor = palette.text.secondary;
                        }
                    }
                    // Dark text for warning
                    if (color === 'warning') {
                        styles.color = palette.text.primary;
                    }
                    // Color overrides for info
                    if (color === 'info') {
                        styles.backgroundColor = palette.other.spaceCadet;
                    }
                    if (variant === 'inline') {
                        styles.position = 'relative';
                        styles.transform = 'none';
                        styles.WebkitTransform = 'none';
                    }
                    return styles;
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root({ theme }) {
                    return {
                        marginBottom: theme.spacing(2),
                        borderRadius: '4px',
                        border: `1px solid ${theme.palette.other.outlineBorder}`,
                        padding: theme.spacing(2),
                        paddingTop: theme.spacing(1),
                        paddingBottom: theme.spacing(1),
                        display: 'flex',
                        gap: theme.spacing(2),
                        backgroundColor: theme.palette.utility.main,
                        color: theme.palette.text.primary,
                        '&:hover': {
                            backgroundColor: theme.palette.utility.dark,
                        },
                        '&>.MuiListItemIcon-root': {
                            minWidth: 'auto',
                        },
                        '& .MuiListItemText-primary': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        },
                        '& .MuiListItemText-secondary': {
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        },
                        [theme.breakpoints.up('md')]: {
                            paddingTop: theme.spacing(2),
                            paddingBottom: theme.spacing(2),
                            '&>.MuiListItemText-root': {
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                gap: theme.spacing(2),
                            },
                            '& .MuiListItemText-primary, .MuiListItemText-secondary': {
                                flex: 1,
                            },
                        },
                    };
                },
            },
        },
        MuiDataGrid: {
            defaultProps: {
                autoHeight: true,
                disableRowSelectionOnClick: true,
            },
            styleOverrides: {
                cell: ({ theme: { palette, typography } }) => ({
                    fontSize: typography.body2.fontSize,
                    color: palette.text.secondary,
                    '&:active,:focus,:focus-within': {
                        outline: 'none',
                    },
                }),
                columnHeader: ({ theme: { palette, typography } }) => ({
                    borderBottom: `solid ${palette.other.outlineBorder} 1.5px`,
                    ...typography.subtitle2,
                    '&:focus,:focus-within,:active': {
                        outline: 'none',
                        '&>.MuiDataGrid-columnSeparator': {
                            opacity: 1,
                        },
                    },
                    '&.MuiDataGrid-columnHeader--siblingFocused': {
                        '& .MuiDataGrid-columnSeparator': {
                            opacity: 1,
                        },
                    },
                    height: '38px !important',
                }),
                'columnHeader--sorted': ({ theme: { palette } }) => ({
                    '& div': { marginBottom: -0.5 },
                    outline: 'none',
                    borderBottom: `solid ${palette.secondary.main} 2px !important`,
                }),
                footerContainer: {
                    backgroundColor: 'transparent',
                    border: 'none',
                },
                main: ({ theme: { palette } }) => ({
                    /* Need to use outline here because Mui doesn't support adding borders to main or virtualScroller */
                    outline: `solid ${palette.other.outlineBorder} 1.5px`,
                    /* Added margin so that outline is always visible */
                    margin: '1.5px',
                    borderRadius: '2px',
                    /*
                      The table column headers don't have a bottom border. Instead, MUI is adding an ::after line and using it as a bottom border.
                      Since we want to update the border to color for a specific column header, I've hidden this ::after line and
                      apply a bottom border in the column header class.
                      MUI Source Code: https://github.com/mui/mui-x/blob/4a77854effb40ad0b13aefc5c16811ca67d46fa4/packages/x-data-grid/src/components/virtualization/GridTopContainer.tsx#L18
                    */
                    '>::after': {
                        height: 0,
                    },
                }),
                root: {
                    border: 'none',
                    borderColor: 'transparent',
                    '.css-1oudwrl::after': {
                        height: 0,
                    },
                    '&.MuiDataGrid-root--densityComfortable': {
                        '& .MuiDataGrid-row': {
                            minHeight: '68px !important',
                        },
                    },
                    '&.MuiDataGrid-root--densityStandard': {
                        '& .MuiDataGrid-row': {
                            minHeight: '52px !important',
                        },
                    },
                },
                row: ({ theme: { palette } }) => ({
                    borderBottom: 'none',
                    backgroundColor: palette.background.paper,
                    '&:hover': {
                        cursor: 'pointer',
                        backgroundColor: palette.grey[50],
                    },
                }),
            },
        },
        MuiInput: {
            styleOverrides: {
                input: ({ theme }) => ({
                    padding: theme.spacing(1),
                }),
            },
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: ({ theme: internalTheme }) => ({
                    borderRadius: '2px',
                    backgroundColor: internalTheme.palette.common.white,
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        border: `1.5px solid${internalTheme.palette.other.outlineBorder}`,
                    },
                    '&.Mui-error': {
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: `1.5px solid${internalTheme.palette.other.outlineBorder}`,
                            borderBottom: `3px solid${internalTheme.palette.error.main}`,
                        },
                    },
                    '&.Mui-focused': {
                        color: internalTheme.palette.other.spaceCadet,
                        '& .MuiOutlinedInput-notchedOutline': {
                            border: `1.5px solid ${internalTheme.palette.other.spaceCadet}`,
                        },
                    },
                }),
                sizeSmall: {
                    padding: `0 !important`,
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                },
            },
        },
        MuiSkeleton: {
            styleOverrides: {
                root: ({ theme: internalTheme }) => ({
                    backgroundColor: alpha(internalTheme.palette.text.primary, 0.09),
                }),
            },
        },
        MuiAccordionSummary: {
            styleOverrides: {
                content: {
                    margin: 0,
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: ({ theme: internalTheme }) => ({
                    fontSize: internalTheme.typography.subtitle2.fontSize,
                    lineHeight: internalTheme.typography.subtitle2.lineHeight,
                    letterSpacing: internalTheme.typography.subtitle2.letterSpacing,
                    fontWeight: internalTheme.typography.subtitle2.fontWeight,
                }),
                action: ({ theme: internalTheme }) => ({
                    padding: internalTheme.spacing(0.5, 0, 0.5, 1),
                    marginLeft: internalTheme.spacing(1),
                    button: {
                        padding: internalTheme.spacing(0.5),
                    },
                }),
                message: ({ theme: internalTheme }) => ({
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    width: '100%',
                    [internalTheme.breakpoints.up('md')]: {
                        flexDirection: 'row',
                        padding: internalTheme.spacing(1, 0),
                        alignItems: 'center',
                    },
                }),
            },
            variants: [
                {
                    props: { color: 'info', variant: 'standard' },
                    style: ({ theme }) => ({
                        border: 'none',
                        color: theme.palette.primary.dark,
                        backgroundColor: theme.palette.primary.light,
                        '& .MuiAlert-icon': {
                            color: theme.palette.primary.main,
                        },
                    }),
                },
                {
                    props: { color: 'success', variant: 'standard' },
                    style: ({ theme }) => ({
                        border: 'none',
                        backgroundColor: theme.palette.success.light,
                        color: theme.palette.success.dark,
                        '& .MuiAlert-icon': {
                            color: theme.palette.success.main,
                        },
                    }),
                },
                {
                    props: { color: 'warning', variant: 'standard' },
                    style: ({ theme }) => ({
                        border: 'none',
                        backgroundColor: theme.palette.warning.light,
                        color: theme.palette.warning.dark,
                        '& .MuiAlert-icon': {
                            color: theme.palette.warning.main,
                        },
                    }),
                },
                {
                    props: { color: 'error', variant: 'standard' },
                    style: ({ theme }) => ({
                        border: 'none',
                        backgroundColor: theme.palette.error.light,
                        color: theme.palette.error.dark,
                        '& .MuiAlert-icon': {
                            color: theme.palette.error.main,
                        },
                    }),
                },
                {
                    props: { color: 'primary', variant: 'standard' },
                    style: ({ theme }) => ({
                        border: 'none',
                        color: theme.palette.primary.dark,
                        backgroundColor: theme.palette.primary.light,
                        '& .MuiAlert-icon': {
                            color: theme.palette.primary.main,
                        },
                    }),
                },
                {
                    props: { color: 'secondary', variant: 'standard' },
                    style: ({ theme }) => ({
                        border: 'none',
                        backgroundColor: theme.palette.secondary.light,
                        color: theme.palette.secondary.dark,
                        '& .MuiAlert-icon': {
                            color: theme.palette.secondary.dark,
                        },
                    }),
                },
            ],
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: '2px',
                },
                sizeLarge: {
                    fontSize: '1rem',
                    fontWeight: 600,
                    letterSpacing: '.15px',
                    lineHeight: '28px',
                },
                sizeMedium: {
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    letterSpacing: '.15px',
                    lineHeight: '28px',
                },
                sizeSmall: {
                    fontSize: '0.813rem',
                    fontWeight: 600,
                    letterSpacing: '.15px',
                    lineHeight: '22px',
                },
                contained({ theme: { palette }, ownerState: { color } }) {
                    const styles = {
                        boxShadow: 'none',
                        '&:hover,:active,:focus': {
                            boxShadow: 'none',
                        },
                    };
                    if (color === 'secondary') {
                        styles.backgroundColor = palette[color].dark;
                        styles.color = palette.common.white;
                        styles[':hover'] = {
                            backgroundColor: darken(palette[color].dark, 0.3),
                        };
                    }
                    if (color === 'warning') {
                        styles.backgroundColor = palette[color].main;
                        styles.color = palette[color].dark;
                        styles[':hover'] = {
                            backgroundColor: darken(palette[color].main, 0.3),
                        };
                    }
                    return styles;
                },
                outlined({ theme: { palette }, ownerState: { color } }) {
                    const styles = {};
                    styles['&.Mui-disabled'] = {
                        border: `1.5px solid ${palette.other.disabledOutlineBorder}`,
                    };
                    switch (color) {
                        case 'secondary':
                        case 'warning':
                            styles.border = `1.5px solid ${palette[color].dark}`;
                            styles.color = palette[color].dark;
                            styles[':hover'] = {
                                border: `1.5px solid ${palette[color].dark}`,
                            };
                            break;
                        case 'primary':
                        case 'info':
                        case 'error':
                        case 'success':
                            styles.border = `1.5px solid ${palette[color].main}`;
                            styles[':hover'] = {
                                border: `1.5px solid ${palette[color].main}`,
                            };
                            break;
                        case 'utility':
                            styles.color = `${palette.text.secondary}`;
                            styles.backgroundColor = `${palette.common.white}`;
                            styles.border = `1.5px solid ${palette.other.outlineBorder}`;
                            styles[':hover'] = {
                                backgroundColor: `${palette.action.hover}`,
                                border: `1.5px solid ${palette.other.outlineBorder}`,
                            };
                            break;
                        case 'inherit':
                            styles.border = '1.5px solid inherit';
                            styles[':hover'] = {
                                border: '1.5px solid inherit',
                            };
                        // no default
                    }
                    return styles;
                },
                text({ theme: { palette }, ownerState: { color } }) {
                    if (color === 'secondary' || color === 'warning') {
                        return { color: palette[color].dark };
                    }
                    return {};
                },
            },
            variants: [
                {
                    props: { color: 'utility', variant: 'contained' },
                    style: ({ theme }) => ({
                        border: `1.5px solid ${theme.palette.other.outlineBorder}`,
                    }),
                },
                {
                    props: { color: 'utility', variant: 'contained', disabled: true },
                    style: {
                        '&:disabled': {
                            backgroundColor: 'transparent',
                        },
                    },
                },
                {
                    props: { color: 'utility', variant: 'contained' },
                    style: ({ theme }) => ({
                        border: `1.5px solid ${theme.palette.other.outlineBorder}`,
                    }),
                },
                {
                    props: { color: 'utility', variant: 'text', disabled: false },
                    style: ({ theme }) => ({
                        color: `${theme.palette.text.secondary}`,
                        ':hover': {
                            backgroundColor: `${theme.palette.action.hover}`,
                        },
                    }),
                },
                // Deprecated - use linkV2 instead
                {
                    props: { variant: 'link', disabled: false },
                    style: ({ theme: { palette } }) => ({
                        color: palette.primary.main,
                        '&:hover,:active,:focus': {
                            backgroundColor: 'transparent',
                            textDecoration: 'underline',
                        },
                    }),
                },
                {
                    props: { variant: 'link', disabled: true },
                    style: ({ theme: { palette } }) => ({
                        color: palette.action.disabled,
                    }),
                },
                {
                    props: { variant: 'linkV2', disabled: false, color: 'primary' },
                    style: ({ theme: { palette } }) => ({
                        color: palette.primary.main,
                        padding: 0,
                        '&:hover,:active,:focus': {
                            color: palette.primary.dark,
                        },
                    }),
                },
                {
                    props: { variant: 'linkV2', disabled: false, color: 'error' },
                    style: ({ theme: { palette } }) => ({
                        color: palette.error.main,
                        padding: 0,
                        '&:hover,:active,:focus': {
                            color: palette.error.dark,
                        },
                    }),
                },
                {
                    props: { variant: 'linkV2', disabled: false, color: 'warning' },
                    style: ({ theme: { palette } }) => ({
                        color: palette.warning.main,
                        padding: 0,
                        '&:hover,:active,:focus': {
                            color: palette.warning.dark,
                        },
                    }),
                },
                {
                    props: { variant: 'linkV2', disabled: false, color: 'success' },
                    style: ({ theme: { palette } }) => ({
                        color: palette.success.main,
                        padding: 0,
                        '&:hover,:active,:focus': {
                            color: palette.success.dark,
                        },
                    }),
                },
                {
                    props: { variant: 'linkV2', disabled: false, color: 'secondary' },
                    style: ({ theme: { palette } }) => ({
                        color: palette.secondary.main,
                        padding: 0,
                        '&:hover,:active,:focus': {
                            color: palette.secondary.dark,
                        },
                    }),
                },
                {
                    props: { variant: 'linkV2', disabled: false, color: 'utility' },
                    style: ({ theme: { palette } }) => ({
                        color: palette.text.secondary,
                        padding: 0,
                        '&:hover,:active,:focus': {
                            color: palette.grey[900],
                        },
                    }),
                },
                {
                    props: { variant: 'linkV2', disabled: true },
                    style: ({ theme: { palette } }) => ({
                        padding: 0,
                        color: palette.action.disabled,
                    }),
                },
            ],
        },
        MuiChip: {
            defaultProps: {
                size: 'medium',
                variant: 'filled',
            },
            variants: [
                {
                    props: { size: 'xs' },
                    style: ({ theme }) => ({
                        height: theme.spacing(2.5),
                        '& .MuiChip-avatarXs': {
                            width: theme.spacing(2),
                            height: theme.spacing(2),
                            fontSize: theme.spacing(1),
                            marginRight: theme.spacing(-0.5),
                            marginLeft: 2,
                        },
                        '& .MuiChip-labelXs': {
                            paddingLeft: theme.spacing(1),
                            paddingRight: theme.spacing(1),
                        },
                        '& .MuiChip-deleteIconXs': {
                            fontSize: theme.spacing(2),
                            marginRight: theme.spacing(0.5),
                            marginLeft: theme.spacing(-0.5),
                        },
                        '& .MuiChip-iconXs': {
                            width: theme.spacing(2),
                            height: theme.spacing(2),
                            fontSize: theme.spacing(1),
                            marginRight: theme.spacing(-0.5),
                            marginLeft: 2,
                        },
                    }),
                },
            ],
            styleOverrides: {
                root({ theme, ownerState: { color, variant } }) {
                    const { palette } = theme;
                    const styles = {
                        fontWeight: 600,
                        '&.Mui-disabled': {
                            opacity: 1,
                            backgroundColor: palette.action.disabledBackground,
                            color: palette.text.disabled,
                            '& .MuiChip-deleteIcon': {
                                color: palette.text.disabled,
                            },
                            '& .MuiChip-icon': {
                                color: palette.text.disabled,
                            },
                            '& .MuiChip-avatar': {
                                backgroundColor: darken(palette.action.disabledBackground, 0.1),
                                color: palette.text.disabled,
                            },
                        },
                    };
                    if (variant === 'filled') {
                        switch (color) {
                            case 'default':
                                styles.backgroundColor = palette.text.secondary;
                                styles.color = palette.getContrastText(palette.text.secondary);
                                break;
                            case 'secondary':
                                styles.backgroundColor = palette.secondary.dark;
                                styles.color = palette.getContrastText(palette.secondary.dark);
                                break;
                            case 'warning':
                                styles.color = palette[color].dark;
                                break;
                            // no default
                        }
                    }
                    if (variant === 'outlined') {
                        // No border
                        styles.borderColor = 'transparent';
                        switch (color) {
                            // Dark text on light background
                            case 'secondary':
                            case 'warning':
                                styles.color = palette[color].dark;
                                styles.backgroundColor = palette[color].light;
                                break;
                            // Main text on light background
                            case 'primary':
                            case 'info':
                            case 'error':
                            case 'success':
                                styles.color = palette[color].main;
                                styles.backgroundColor = palette[color].light;
                                break;
                            case 'default':
                                styles.color = palette.text.secondary;
                                styles.backgroundColor = palette.grey[200];
                                break;
                            // no default
                        }
                    }
                    return styles;
                },
                avatar({ theme: { palette }, ownerState: { color, variant } }) {
                    const styles = {};
                    if (variant === 'filled') {
                        switch (color) {
                            case 'default':
                                styles.backgroundColor = palette.grey[900];
                                styles.color = palette.getContrastText(palette.grey[900]);
                                break;
                            case 'secondary':
                                styles.backgroundColor = palette.secondary.main;
                                styles.color = palette.getContrastText(palette.secondary.main);
                                break;
                            case 'primary':
                            case 'info':
                            case 'error':
                            case 'warning':
                                styles.backgroundColor = palette[color].dark;
                                styles.color = palette.getContrastText(palette[color].dark);
                                break;
                            case 'success':
                                styles.backgroundColor = palette[color].dark;
                                styles.color = palette.getContrastText(palette[color].dark);
                                break;
                            // no default
                        }
                    }
                    if (variant === 'outlined') {
                        switch (color) {
                            case 'default':
                                styles.backgroundColor = palette.text.secondary;
                                styles.color = palette.getContrastText(palette.text.secondary);
                                break;
                            case 'primary':
                            case 'secondary':
                            case 'info':
                            case 'warning':
                                styles.backgroundColor = palette[color].dark;
                                styles.color = palette.getContrastText(palette[color].dark);
                                break;
                            case 'error':
                            case 'success':
                                styles.backgroundColor = palette[color].main;
                                styles.color = palette.getContrastText(palette[color].main);
                                break;
                            // no default
                        }
                    }
                    return styles;
                },
                deleteIcon({ theme: { palette }, ownerState: { variant, color } }) {
                    const styles = {};
                    const resting = (color) => alpha(color, 0.8);
                    if (variant === 'filled') {
                        switch (color) {
                            case 'default':
                                styles.color = resting(palette.secondary.contrastText);
                                styles[':hover'] = { color: palette.secondary.contrastText };
                                break;
                            case 'warning':
                                styles.color = palette[color].dark;
                                break;
                            // no default
                        }
                    }
                    if (variant === 'outlined') {
                        switch (color) {
                            case 'default':
                                styles.color = resting(palette.text.secondary);
                                styles[':hover'] = { color: palette.text.secondary };
                                break;
                            case 'primary':
                                styles.color = resting(palette.primary.dark);
                                styles[':hover'] = { color: palette.primary.dark };
                                break;
                            case 'secondary':
                                styles.color = resting(palette.secondary.dark);
                                styles[':hover'] = { color: palette.secondary.dark };
                                break;
                            case 'warning':
                                styles.color = resting(palette.warning.dark);
                                styles[':hover'] = { color: palette.warning.dark };
                                break;
                            // no default
                        }
                    }
                    return styles;
                },
                icon({ theme: { palette }, ownerState: { variant, color } }) {
                    const styles = {};
                    if (variant === 'filled') {
                        styles.color = palette.getContrastText(palette.text.secondary);
                        switch (color) {
                            case 'default':
                                styles.color = palette.getContrastText(palette.text.secondary);
                                break;
                            case 'warning':
                                styles.color = palette[color].dark;
                                break;
                            // no default
                        }
                    }
                    if (variant === 'outlined') {
                        switch (color) {
                            case 'default':
                                styles.color = palette.text.secondary;
                                break;
                            case 'primary':
                                styles.color = palette.primary.dark;
                                break;
                            case 'secondary':
                                styles.color = palette.secondary.dark;
                                break;
                            case 'warning':
                                styles.color = palette.warning.dark;
                                break;
                            // no default
                        }
                    }
                    return styles;
                },
                sizeMedium: ({ theme: internalTheme }) => ({
                    height: internalTheme.spacing(4),
                }),
                sizeSmall: ({ theme: internalTheme }) => ({
                    height: internalTheme.spacing(3),
                }),
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: ({ theme: internalTheme }) => ({
                    fieldset: {
                        border: `1.5px solid${internalTheme.palette.other.outlineBorder}`,
                    },
                    '&.highlight-secondary fieldset, &:hover.highlight-secondary fieldset': {
                        border: `1.5px solid${internalTheme.palette.other.outlineBorder}`,
                        borderBottom: `3px solid${internalTheme.palette.secondary.main}`,
                    },
                }),
            },
        },
        MuiTooltip: {
            defaultProps: {
                leaveTouchDelay: 2500,
            },
        },
        MuiDivider: {
            variants: [
                {
                    props: { variant: 'dashed' },
                    style: {
                        '::before': {
                            borderTopStyle: 'dashed',
                        },
                        '::after': {
                            borderTopStyle: 'dashed',
                        },
                    },
                },
            ],
        },
        MuiCardHeader: {
            styleOverrides: {
                action: {
                    marginTop: 'auto',
                    marginBottom: 'auto',
                },
            },
        },
        MuiFormLabel: {
            styleOverrides: {
                root: {
                    fontSize: '0.875rem',
                },
            },
        },
        MuiSelect: {
            styleOverrides: {
                select: {
                    minHeight: 'unset',
                    lineHeight: '1.5 rem',
                },
            },
        },
        MuiSwitch: {
            defaultProps: {
                variant: 'default',
                size: 'small',
            },
            variants: [
                {
                    props: { variant: 'default' },
                    style: ({ theme }) => ({
                        '& .MuiSwitch-thumb': {
                            boxShadow: '0px 1px 1px 0px #0000000F, 0px 3px 8px 0px #00000026',
                        },
                        '& .MuiSwitch-switchBase': {
                            color: theme.palette.common.white,
                            '& + .MuiSwitch-track': {
                                backgroundColor: '#D0D5DD',
                            },
                            '&:hover': {
                                '& + .MuiSwitch-track': {
                                    backgroundColor: darken('#D0D5DD', 0.1),
                                },
                            },
                            '&.Mui-checked': {
                                color: theme.palette.common.white,
                                '& + .MuiSwitch-track': {
                                    opacity: 1,
                                    backgroundColor: theme.palette.primary.main,
                                },
                                '&:hover': {
                                    '& + .MuiSwitch-track': {
                                        backgroundColor: theme.palette.primary.dark,
                                    },
                                },
                            },
                            '&.Mui-disabled': {
                                color: `${lighten(theme.palette.other.disabledOutlineBorder, 0.2)} !important`,
                                '& + .MuiSwitch-track': {
                                    opacity: 1,
                                    backgroundColor: '#D0D5DD',
                                },
                            },
                        },
                    }),
                },
                {
                    props: { variant: 'default', size: 'small' },
                    style: ({ theme }) => ({
                        width: 42,
                        height: 23,
                        // Replace padding with margin so the thumb is still within the switch
                        padding: 0,
                        margin: theme.spacing(0.5),
                        '&:active': {
                            '& .MuiSwitch-thumb': {
                                width: 20,
                            },
                        },
                        '& .MuiSwitch-switchBase': {
                            padding: 0,
                            margin: 1.5,
                            '&.Mui-checked': {
                                transform: 'translateX(19px)',
                            },
                        },
                        '& .MuiSwitch-thumb': {
                            width: 20,
                            height: 20,
                            boxSizing: 'border-box',
                            transition: theme.transitions.create(['width'], {
                                duration: 200,
                            }),
                        },
                        '& .MuiSwitch-track': {
                            borderRadius: 11.5,
                            opacity: 1,
                            boxSizing: 'border-box',
                        },
                    }),
                },
                {
                    props: { variant: 'default', size: 'large' },
                    style: ({ theme }) => ({
                        width: 52,
                        height: 32,
                        // Replace padding with margin so the thumb is still within the switch
                        padding: 0,
                        margin: '6px',
                        '&:active': {
                            '& .MuiSwitch-thumb': {
                                width: 28,
                            },
                        },
                        '& .MuiSwitch-switchBase': {
                            padding: 0,
                            margin: 2,
                            '&.Mui-checked': {
                                transform: 'translateX(20px)',
                            },
                        },
                        '& .MuiSwitch-thumb': {
                            width: 28,
                            height: 28,
                            boxSizing: 'border-box',
                            transition: theme.transitions.create(['width'], {
                                duration: 200,
                            }),
                        },
                        '& .MuiSwitch-track': {
                            borderRadius: 16,
                            opacity: 1,
                            boxSizing: 'border-box',
                        },
                    }),
                },
            ],
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: ({ theme: internalTheme, ownerState: { selected } }) => {
                    return {
                        border: `solid 0.075rem ${selected
                            ? internalTheme.palette.primary.main
                            : internalTheme.palette.grey.A100}`,
                        backgroundColor: selected
                            ? internalTheme.palette.primary.main
                            : internalTheme.palette.common.white,
                    };
                },
                ellipsis: {
                    border: 'none',
                    backgroundColor: 'transparent',
                },
            },
        },
        MuiFormHelperText: {
            styleOverrides: {
                root: {
                    lineHeight: 1.167,
                    paddingTop: 4,
                },
            },
        },
        MuiToggleButton: {
            styleOverrides: {
                root: ({ theme }) => ({
                    '&.MuiToggleButton-root': {
                        borderColor: theme.palette.other.outlineBorder,
                        borderRadius: '0',
                        borderWidth: '1.5px',
                        color: theme.palette.text.secondary,
                        gap: theme.spacing(1),
                    },
                }),
                sizeLarge: ({ theme }) => ({
                    '&.MuiToggleButton-root': {
                        maxHeight: 44,
                        minHeight: 44,
                        minWidth: 56,
                        paddingBottom: theme.spacing(1.25),
                        paddingLeft: theme.spacing(2),
                        paddingRight: theme.spacing(2),
                        paddingTop: theme.spacing(1.25),
                    },
                }),
                sizeMedium: ({ theme }) => ({
                    '&.MuiToggleButton-root': {
                        maxHeight: 40,
                        minHeight: 40,
                        minWidth: 56,
                        paddingBottom: theme.spacing(1),
                        paddingLeft: theme.spacing(2),
                        paddingRight: theme.spacing(2),
                        paddingTop: theme.spacing(1),
                    },
                }),
                sizeSmall: ({ theme }) => ({
                    '&.MuiToggleButton-root': {
                        maxHeight: 30,
                        minHeight: 30,
                        minWidth: 52,
                        paddingBottom: theme.spacing(0.5),
                        paddingLeft: theme.spacing(2),
                        paddingRight: theme.spacing(2),
                        paddingTop: theme.spacing(0.5),
                    },
                }),
            },
            variants: [
                {
                    props: { disabled: true },
                    style: ({ theme }) => ({
                        '&.MuiToggleButton-root.Mui-disabled': {
                            backgroundColor: theme.palette.background.default,
                            borderColor: theme.palette.other.disabledOutlineBorder,
                            color: theme.palette.text.disabled,
                        },
                    }),
                },
                {
                    props: { selected: true },
                    style: ({ theme }) => ({
                        '&.MuiToggleButton-root.Mui-selected': {
                            backgroundColor: theme.palette.action.selected,
                            borderColor: theme.palette.other.outlineBorder,
                            color: theme.palette.primary.main,
                        },
                    }),
                },
            ],
        },
        MuiToggleButtonGroup: {
            defaultProps: {
                exclusive: true,
            },
            styleOverrides: {
                grouped: ({ theme }) => ({
                    borderRadius: '1.5px !important',
                    borderWidth: '1.5px',
                    '&.Mui-selected + .MuiToggleButtonGroup-grouped.Mui-selected': {
                        borderLeft: `1.5px solid ${theme.palette.other.outlineBorder}`,
                        marginLeft: '-1.5px !important',
                    },
                }),
                firstButton: {
                    borderBottomRightRadius: '0 !important',
                    borderTopRightRadius: '0 !important',
                },
                middleButton: {
                    borderBottomLeftRadius: '0 !important',
                    borderBottomRightRadius: '0 !important',
                    borderLeft: '1.5px solid transparent',
                    borderTopLeftRadius: '0 !important',
                    borderTopRightRadius: '0 !important',
                    marginLeft: -1.5,
                    '&.Mui-disabled': {
                        borderLeft: '1.5px solid transparent',
                    },
                },
                lastButton: {
                    borderBottomLeftRadius: '0 !important',
                    borderLeft: '1.5px solid transparent',
                    borderTopLeftRadius: '0 !important',
                    marginLeft: -1.5,
                    '&.Mui-disabled': {
                        borderLeft: '1.5px solid transparent',
                    },
                },
            },
            variants: [
                {
                    props: { size: 'large' },
                    style: ({ theme }) => ({
                        '& .MuiToggleButton-root': {
                            paddingBottom: theme.spacing(1.25),
                            paddingLeft: theme.spacing(2),
                            paddingRight: theme.spacing(2),
                            paddingTop: theme.spacing(1.25),
                        },
                    }),
                },
                {
                    props: { size: 'medium' },
                    style: ({ theme }) => ({
                        '& .MuiToggleButton-root': {
                            paddingBottom: theme.spacing(1),
                            paddingLeft: theme.spacing(2),
                            paddingRight: theme.spacing(2),
                            paddingTop: theme.spacing(1),
                        },
                    }),
                },
                {
                    props: { size: 'small' },
                    style: ({ theme }) => ({
                        '& .MuiToggleButton-root': {
                            paddingBottom: theme.spacing(0.5),
                            paddingLeft: theme.spacing(2),
                            paddingRight: theme.spacing(2),
                            paddingTop: theme.spacing(0.5),
                        },
                    }),
                },
            ],
        },
    },
});
export const theme = responsiveFontSizes(settings);
